import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useContext, useState, useEffect } from 'react';
import { COSMOS_API, POST_ENDPOINT } from '../api/API';
import { AdminContext } from '../contexts/AdminContext';
import { ApplicationData, ClaimValue, Claims, ExternalEnumRecord, claimEnabledInContext, claimsListToSchema, makeExternalEnumRecord } from '../interfaces/APIInterface';
import { OptionsType } from '../interfaces/ApplicationInterfaces';
import { ORG_SHARED_DATA } from '../interfaces/UserInterfaces';
import { AppEnvSetting, generateOptionType } from '../ts/common';
import APPSETTINGS from '../settings.json';
import { ApplicationClaimField } from './ApplicationClaimField';
import { EnqueueSnackbar } from 'notistack';

interface AddOrgToUserProps {
  open: boolean;
  handleClose: () => void;
  userId: string;
  country: string;
  provisionableOrgs:  Array<{ id: string; name: string }>;
  fetchUserData: () => void
  appExternalEnums: Record<string, Record<string, ExternalEnumRecord>>;
  setAppExternalEnums: React.Dispatch<React.SetStateAction<Record<string, Record<string, ExternalEnumRecord>>>>;
  enqueueSnackbar: EnqueueSnackbar
}

export const AddOrgToUser = (props: AddOrgToUserProps) => {
  const { adminCredentials, getToken } = useContext(AdminContext);
//   const [organizations, setOrganizations] = useState<
//     Array<{ id: string; name: string }>
//   >([]);
  const [organizationToEdit, setOrganizationToEdit] = useState<string>(
    adminCredentials.organizationId
  );
  const [orgLevelClaims, setOrgLevelClaims] = useState<Array<Claims>>([]);
  const [applications, setApplications] = useState<Array<OptionsType>>([]);
  const [selectedApps, setSelectedApps] = useState<Array<OptionsType>>([]);
  const [appClaims, setAppClaims] = useState<Record<string, Array<Claims>>>({});
  const [orgData, setOrgData] = useState<Record<string, Record<string, string>>>({ [ORG_SHARED_DATA]: {} });

  useEffect(() => {
    /* https://stackoverflow.com/a/60907638 */

      if (adminCredentials.isSuperAdmin || adminCredentials.multiOrg) {
        //let orgList = adminCredentials.organizationList.filter((org) => !props.currentOrgs.includes(org.id));
        // setOrganizations(orgList);
        if (props.provisionableOrgs.length > 0) 
            updateOrgData(props.provisionableOrgs[0].id);
        else
            props.handleClose();

      } else {
        props.handleClose();
      }
  }, []);

  async function updateOrgData(orgId: string) {

    let org: Record<string, Record<string, string>> = { [ORG_SHARED_DATA]: {} };

    setOrganizationToEdit(orgId);
    setOrgData(org);
    setSelectedApps([]);
    let orgDataResp = await COSMOS_API.POST(
      { id: orgId },
      POST_ENDPOINT.GetOrganizationData,
      getToken()
    );

    if (orgDataResp.ok && orgDataResp.respBody !== undefined && orgDataResp.respBody !== null) {
      setOrgLevelClaims(orgDataResp.respBody.claims);
    }

    if (adminCredentials.isSuperAdmin || adminCredentials.multiOrg) {
      let apps: Array<OptionsType> = [];
      apps = Object.keys(adminCredentials.allApplications).reduce(function (
        result,
        appId
      ) {
        if ((orgDataResp.respBody.appList && orgDataResp.respBody.appList.includes(appId)) ||
        ((appId === APPSETTINGS.msalClientId) && adminCredentials.generalPermissions.manageAdmins)){
          result.push(
            generateOptionType(adminCredentials.allApplications[appId]?.name, appId)
          );
        }

        return result;
      },
      apps);

      setApplications([...apps]);
    }    
  }

  const changeInSelectedApplications = async (e: Array<OptionsType>) => {
    var newApps: Record<string, Record<string, string>> = orgData
      ? structuredClone(orgData)
      : { [ORG_SHARED_DATA]: {} };

    setSelectedApps([...e]);

    if (Object.keys(newApps).length > 1) {
      Object.keys(newApps).forEach((app) => {
        if (
          app !== ORG_SHARED_DATA &&
          e.find((newApp) => newApp.value === app) === undefined
        ) {
          delete newApps[app];
        }
      });
    }

    var newAppClaims = structuredClone(appClaims);

    Object.keys(newAppClaims).forEach((currentApp) => {
      if (e.find((app) => app.value === currentApp) === undefined) {
        delete newAppClaims[currentApp];
      }
    });

    for (const [index, selected] of e.entries()) {
      if (newAppClaims[selected.value] === undefined) {
        var appData: ApplicationData = (await COSMOS_API.POST(
          { id: selected.value },
          POST_ENDPOINT.GetApplicationData,
          getToken()
        )).respBody;
        newAppClaims[selected.value] = appData && appData.claims ? appData.claims : [];
        if ((appData.claims.some(clm => !!clm.externalEnumeration)) && (!Object.keys(props.appExternalEnums).includes(selected.value))) {
          var respData: Record<string, Array<ClaimValue>> = (await COSMOS_API.POST({ id: selected.value, orgId: organizationToEdit},
            POST_ENDPOINT.GetApplicationExternalEnumerations,
            getToken()
          )).respBody;
          props.setAppExternalEnums({
            ...props.appExternalEnums,
            [selected.value]: makeExternalEnumRecord(respData)
          });
        }
      }
      if (
        orgData !== undefined &&
        orgData !== undefined
      ) {
        /* if (applicationData !== undefined && applicationData.name === complexApps.learningCompass)
                    brandsNeeded = true; */
        newApps[selected.value] = {
          ...orgData[selected.value],
        };
      } else {
        newApps[selected.value] = {
          userProvisionStatus: 'Approved',
        };
      }
    }
    setAppClaims(newAppClaims);

    setOrgData(newApps);
  };

  const updateClaim = (targetApp: string, claimName: string, claimValue: string) => {
    var newApps: Record<string, Record<string, string>> = structuredClone(orgData);

    if(newApps[targetApp] === undefined || newApps[targetApp] === null){
        newApps[targetApp] = {};
    }

    newApps[targetApp][claimName] = claimValue;

    setOrgData(newApps);
  }

  const provisionOrg = async () => {
    var body = {
        id: props.userId,
        country: props.country,
        organizationId: organizationToEdit,
        orgData: orgData
    }

    let res = await COSMOS_API.POST(body, POST_ENDPOINT.SaveUserOrganizationData, getToken())
    if(res.ok)
        await props.fetchUserData();
    else {
      props.enqueueSnackbar(`Could not add organization to user: ${res?.respBody?.message}`, {variant: 'error'});
        console.log(res);
        console.log(JSON.stringify(res));
    }
  }

  return (
    <Dialog open={props.open} onClose={props.handleClose} maxWidth={'xl'} fullWidth>
      <DialogTitle>Add User to new Organization</DialogTitle>
      <DialogContent>
     <Stack spacing={2} sx={{marginTop: '1.5rem'}}>
          <Autocomplete
                      id="org"
                      fullWidth
                      value={props.provisionableOrgs.find(org => org.id === organizationToEdit)}
                      isOptionEqualToValue={(
                        option: {id: string, name: string},
                        value: {id: string, name: string}
                      ) => option.id === value.id}
                      options={props.provisionableOrgs}
                      getOptionLabel={(option:  {id: string, name: string}) => option.name}
                      renderInput={(params) => (
                        <TextField {...params} label={`Organization`} />
                      )}
                      onChange={(
                        _: any,
                        value: {id: string, name: string} | null
                      ) => {
                        if (value === null) return;
                        updateOrgData(value.id);
                      }}
                    />
          {(!AppEnvSetting.disableAppProvisioning) && (
                    <Autocomplete
                      id="applications"
                      fullWidth
                      multiple
                      value={selectedApps}
                      isOptionEqualToValue={(
                        option: OptionsType,
                        value: OptionsType
                      ) => option.value === value.value}
                      options={applications}
                      getOptionLabel={(option: OptionsType) => option.label}
                      renderInput={(params) => (
                        <TextField {...params} label={`Application`} />
                      )}
                      onChange={(
                        _: any,
                        value: OptionsType | OptionsType[] | null
                      ) => {
                        if (value === null) return;
                        if (!Array.isArray(value)) value = [value];
                        changeInSelectedApplications(value);
                      }}
                    />
          )}
              
                        {orgData !== undefined && orgLevelClaims !== undefined && orgLevelClaims.length > 0 ? (
                    <>
                    <Typography>Organization Wide Claims</Typography>
                        <Grid container spacing={2}>
                                  {orgLevelClaims.map((claim) => {
                                    return (
                                      <Grid item xs={12} md={6} key={claim.id}>
                                          <TextField
                                            id={claim.id}
                                            label={claim.id}
                                            value={orgData['sharedData'][claim.id] ?? ''}
                                            onChange={(e) => updateClaim('sharedData', claim.id, e.target.value)}
                                            />
                                      </Grid>)
                                          })}                                   
                                </Grid>
                    </>): (
                              <></>
                            )}
                            {orgData !== undefined && (!AppEnvSetting.disableAppProvisioning) && appClaims !== undefined && Object.keys(appClaims).some(appKey => appClaims[appKey].length > 0) ? (
                    <>                    
                    <Typography>Application Claims</Typography>
                        <Stack spacing={2}>
                            {Object.keys(appClaims).map((appKey) => {
                              if (
                                Object.keys(appClaims[appKey]).length === 0 ||
                                orgData === undefined ||
                                orgData[appKey] ===
                                  undefined
                              )
                                return [];
                              return <div key={appKey}>
                              <Typography>{selectedApps.find((app) => app.value === appKey)!.label} Claims</Typography>
                              <Grid container spacing={2}>
                                  {appClaims[appKey].filter(ac => claimEnabledInContext(ac, claimsListToSchema(appClaims[appKey]), ac.externalEnumeration ? (props.appExternalEnums?.[appKey]?.[ac.id]?.values ?? []) : ac.possibleValues, orgData[appKey])).map((claim) => {
                                    return (
                                      <Grid item key={claim.id} xs={12} md={4}>
                                        <ApplicationClaimField
                                          disabled={false}
                                          claim={claim}
                                          claimValue={orgData[appKey][claim.id]}
                                          setClaimValue={(aid, cid, cvalue) => updateClaim(aid, cid, cvalue)}
                                          appId={appKey}
                                          appExternalEnums={props.appExternalEnums}
                                          claimContext={orgData[appKey]}
                                          claimSchema={claimsListToSchema(appClaims[appKey])}
                                        />
                                      </Grid>
                                    )
                                  })}
                              </Grid>
                               </div>
                            })}                                 
                            </Stack>
                    </>):<></>}
     </Stack>
      </DialogContent>
      <DialogActions>
      <Button
                onClick={props.handleClose}
              >
                Cancel
              </Button>
      <Button
                disabled={(organizationToEdit != null) && (!AppEnvSetting.disableAppProvisioning) && (JSON.stringify(orgData) === JSON.stringify({ [ORG_SHARED_DATA]: {} }))}
                onClick={provisionOrg}
              >
                Add Organization
              </Button>
      </DialogActions>
    </Dialog>
  );
};
